/* eslint-disable react/display-name */
import { navigate } from "gatsby"
import { useEffect, useMemo, useState } from "react"

import {
  marketingClient,
  orderClient,
  useMutation,
  useQueryClient,
} from "@trueskin-web/apis"
import { formatCurrency, isFloat } from "@trueskin-web/functions"
import { Routes } from "@trueskin-web/translations"

import useCurrentPresets from "../../../hooks/use-current-presets"
import { plans as plansData } from "../resources"

const mapCurrentPresetsToPlans = (presets) => {
  return presets.map(
    ({ occurrence, summary, details, id, isRecommended, isSelected }) => {
      const isSubscription = occurrence !== "occ_once"
      const { total, totalWithoutDiscounts, currency } =
        summary.values.perInstalment
      const { count: instalments } = summary.instalments
      const price = total / 100
      let priceWithoutDiscounts = totalWithoutDiscounts / 100

      if (priceWithoutDiscounts <= price) {
        priceWithoutDiscounts = ""
      }

      const priceTitle = formatCurrency(price, currency, isFloat(price) ? 2 : 0)
      const detailsWithUpdatedContent = (plansData[id]?.details || []).map(
        (detail) => ({
          ...detail,
          content: detail.content.replace("XX", price * 2),
        })
      )

      return {
        id,
        isSubscription,
        instalments,
        title: details.name,
        priceTitle,
        priceWithoutDiscounts,
        description: details.description || plansData[id],
        image: details.image,
        details: detailsWithUpdatedContent,
        isRecommended,
        isSelected,
      }
    }
  )
}

const withPresets = (Component) => (props) => {
  const queryClient = useQueryClient()

  const {
    data: { data: currentPresets = [] } = {},
    status: currentPresetsStatus,
    error: currentPresetsError,
  } = useCurrentPresets()

  const {
    mutateAsync: setCurrentPreset,
    status: setCurrentPresetStatus,
    error: setCurrentPresetError,
  } = useMutation(orderClient.setCurrentPreset, {
    onSuccess: ({ data }) =>
      (async ({ customer, preset, values }) => {
        const marketingEventData = {
          userId: customer,
          presetId: preset,
          currency: values.currency,
          value: values.total,
        }

        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.PRODUCT_SELECTED,
          data: marketingEventData,
        })
        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.SELECT_PAYMENT_OPTION,
          data: marketingEventData,
        })

        await Promise.all([
          queryClient.invalidateQueries("currentPresets"),
          queryClient.invalidateQueries("currentOrderSummary"),
        ])
      })(data),
  })
  const [selectedPlanId, setSelectedPlanId] = useState()

  const handleSelect = (planId) => {
    setSelectedPlanId(planId)

    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.PRODUCT_CLICKED,
      data: {
        presetId: planId,
      },
    })
  }

  const handleToggle = (count) => {
    const selectedPlan = plans.find((plan) => plan.id === selectedPlanId)

    if (!selectedPlan.isSubscription) {
      return
    }

    const planMatch = plans.find(
      (plan) => plan.title === selectedPlan.title && plan.instalments === count
    )
    setSelectedPlanId(planMatch.id)
  }

  const handleSubmit = async (planId) => {
    await setCurrentPreset(planId)
    navigate(Routes.App.Start.Shipping.url)
  }

  const plans = useMemo(
    () => mapCurrentPresetsToPlans(currentPresets),
    [currentPresets]
  )

  useEffect(() => {
    setSelectedPlanId(currentPresets.find(({ isSelected }) => isSelected)?.id)
  }, [currentPresets])

  return (
    <Component
      plans={plans}
      selectedPlanId={selectedPlanId}
      isLoading={currentPresetsStatus === "loading"}
      isSubmitting={setCurrentPresetStatus === "loading"}
      error={currentPresetsError || setCurrentPresetError}
      onSelect={handleSelect}
      onSubmit={handleSubmit}
      onToggle={handleToggle}
      {...props}
    />
  )
}

export default withPresets
